import React from 'react';
import { AlertCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Button } from '@/shared/ui/Button';
import { cn } from '@/utils/cn';
import styles from './ErrorState.module.css';

export interface ErrorStateProps {
  title?: string;
  description?: string;
  onRetry?: () => void;
  supportLink?: string;
  icon?: LucideIcon;
  variant?: 'page' | 'section';
  className?: string;
}

const ErrorState = React.forwardRef<HTMLDivElement, ErrorStateProps>(
  (
    {
      title = 'Something went wrong',
      description = 'We encountered an error while loading this content. Please try again.',
      onRetry,
      supportLink,
      icon: Icon = AlertCircle,
      variant = 'page',
      className
    },
    ref
  ) => {
    return (
      <div ref={ref} className={cn(styles.errorState, className)} data-variant={variant} data-testid="error-state">
        <Icon className={styles.icon} size={48} aria-hidden="true" />
        <div className={styles.content}>
          <h3 className={styles.title}>{title}</h3>
          <p className={styles.description}>{description}</p>
        </div>
        {(onRetry || supportLink) && (
          <div className={styles.actions}>
            {onRetry && (
              <Button variant="primary" onClick={onRetry}>
                Try again
              </Button>
            )}
            {supportLink && (
              <Button variant="tertiary" asChild>
                <a href={supportLink} target="_blank" rel="noopener noreferrer">
                  Contact support
                </a>
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }
);

ErrorState.displayName = 'ErrorState';

export { ErrorState };
